/**
 * Turning a failed request into something a user should actually read.
 */

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.'

/**
 * Human-readable message for a failed API request.
 *
 * The API client normalises every error response onto `error.apiError`
 * (see src/api/client.js), covering both the JSON:API `errors[]` envelope and
 * the flat DRF `{ detail }` shape. Reaching past that to `error.message`
 * yields axios's own text - "Request failed with status code 403" - which is
 * never something to show a user. That is exactly what a plan-limit rejection
 * used to surface: a 403 toast with no mention of the limit or how to fix it.
 *
 * @param {Error} error - The rejected axios error.
 * @param {string} fallback - Copy to use when the response carried no message.
 * @returns {string} A message suitable for display.
 */
export function getApiErrorMessage(error, fallback = DEFAULT_MESSAGE) {
  return (
    error?.apiError?.errors?.[0]?.detail ||
    error?.apiError?.message ||
    error?.response?.data?.errors?.[0]?.detail ||
    error?.response?.data?.detail ||
    fallback
  )
}

const ATTRIBUTE_POINTER = /^\/data\/attributes\/(.+)$/

/**
 * The first error for each field a failed request named, e.g.
 * `{ current_password: 'Your current password is incorrect.' }`.
 *
 * Field errors arrive in the JSON:API envelope with a
 * `source.pointer` of `/data/attributes/<field>`; showing them under the field
 * they are about beats one generic toast. Errors without a pointer are left
 * for getApiErrorMessage.
 *
 * @param {Object} errorOrPayload - A rejected axios error (with `apiError`), or
 *   the normalised payload itself, as a thunk's `rejectWithValue` hands it back.
 * @returns {Object<string, string>} field name -> message
 */
export function getApiFieldErrors(errorOrPayload) {
  const errors = errorOrPayload?.apiError?.errors || errorOrPayload?.errors || []
  return errors.reduce((fields, error) => {
    const match = ATTRIBUTE_POINTER.exec(error?.source?.pointer || '')
    if (!match || fields[match[1]]) return fields
    return { ...fields, [match[1]]: error.detail }
  }, {})
}

export default getApiErrorMessage
